import {
  festhaltenLesen,
  MARKIER_STUFE_LABELS,
  markierTage,
  tagKurz,
  TASK_PRIORITIES,
  ZEITFENSTER,
  ZEITFENSTER_KURZ,
  ZEITFENSTER_LABELS,
  type Festgehalten,
  type TaskPriority,
  type Zeitfenster,
} from '@netcon-work/shared'
import { useQueryClient } from '@tanstack/react-query'
import { Plus } from 'lucide-react'
import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react'
import { useNotizAblage } from '../../notizbuch'
import { cn } from '../../lib/cn'
import { heuteSchluessel, useDarfMarkieren, useMarkieren } from '../../lib/markierung'
import { nebenBestandVerwerfen, useStatusSetzen } from '../../lib/mutationen'
import {
  notizenZurAufgabeQuery,
  useMitschreiben,
  type BlattKennung,
  type NotizZurAufgabe,
} from '../../lib/mywork'
import { trpc } from '../../lib/trpc'
import type { StromTermin } from './strom'
import { terminKurz, zieleBestimmen, type Ziel } from './ziel'

/**
 * **Festhalten** – ein Feld für Notizen und Aufgaben
 * ([Z-24](../../../../../docs/phasen/zusatz-24-my-work.md) Stufe 2, E-453).
 *
 * Was es wird, liest `festhaltenLesen` (☐ = Aufgabe, `?` Frage, `!` Beschluss, sonst Notiz); wohin
 * es gehört, `zieleBestimmen` (laufender Termin › Puls › Termin mit der genannten Person › Zettel).
 * Beides steht als Chips unter dem Feld, **bevor** man ↩ drückt.
 *
 * - **☐ legt sofort eine Work-Aufgabe an** (Nils, 23.10.2026: *„finde ich gut"*) – für mich, in der
 *   persönlichen Welt; ist das Ziel die Aufgabe im Puls, als ihre Unteraufgabe. 10 Sekunden
 *   „rückgängig": die Aufgabe wird zurückgestellt (wie eine entfernte Outlook-Kennzeichnung), die
 *   Unteraufgabe gelöscht.
 * - **☐ @Name …** ist für jemand anderen: nur notiert, nie zugewiesen (E-433).
 * - **Am Termin** geht alles als Zeile mit Zeitmarke ins Blatt des Termins (`mitschreiben`, AP
 *   24.23) – ein ☐ steht dort als „☐ … · in Work". Ohne Netz wird es eine eigene Notiz am Termin.
 * - Alles andere geht als Notiz ins Notizbuch (`useNotizAblage`) – an der Aufgabe oder als Zettel.
 *
 * Tasten: ↩ ablegen · ⌘↩ ablegen und ☀ heute · ⇧↩ neue Zeile · ⇥ nächstes Ziel · Esc leeren.
 *
 * **Notiz oder Aufgabe ist auch ein Schalter** (Nils, 24.10.2026: *„Das Aufgaben anlegen müsste
 * noch besser sein, dass man heute usw. – also wie eine schnelle Aufgabe – auch hat. Und den Typ
 * Aufgabe auch wählen statt nur mit den eckigen Klammern."*). Ist es eine Aufgabe, stehen
 * dieselben Fragen wie bei ⚡ Schnelle Aufgabe darunter: **Wann** (Zeitfenster oder Datum),
 * **Priorität**, **☀ Mein Tag**. Was im Text steht (`bis Do`, `heute`), ist dort schon gewählt;
 * ein Klick überstimmt es.
 */

type Typ = 'auto' | 'notiz' | 'aufgabe'

interface Meldung {
  text: string
  rueckgaengig?: () => void
}

export function Festhalten({
  laufend,
  kommende,
  puls,
  meineWelt,
  ichId,
  blatt = false,
  autoFocus = false,
  amTermin,
  blattZu,
  eingebettet = false,
  welten = [],
  weltVorschlag = null,
}: {
  laufend: StromTermin | null
  kommende: readonly StromTermin[]
  puls: { id: string; titel: string } | null
  meineWelt: { id: string } | null
  /**
   * **Meine eigenen Welten** (Nils, 24.10.2026: *„die hätte in meinen privaten gemusst"*) –
   * mehr als eine, dann steht „Wo" im Formular.
   */
  welten?: readonly { id: string; label: string; icon?: string | null }[]
  /** Vorschlag für „Wo" – am Samstag die Welt, für die heute als einzige Zeit ist. */
  weltVorschlag?: string | null
  ichId: string | null
  /** Am Telefon als Blatt von unten: größere Schrift, Knopf statt Hinweiszeile. */
  blatt?: boolean
  autoFocus?: boolean
  /** Eine ☐-Aufgabe zum Termin: der feste Bezug (Z-24 AP 24.16). */
  amTermin?: (taskId: string, termin: StromTermin) => void
  /** Das Blatt eines Termins, wenn das Notizbuch es schon kennt (aus der Tageslage). */
  blattZu?: (terminId: string) => BlattKennung | null
  /**
   * **In der laufenden Terminkarte** (AP 24.23): das Ziel steht fest, keine Taste N, eine Leiste
   * ☐ · ! · ? · @ statt der Hinweiszeile.
   */
  eingebettet?: boolean
}) {
  const [text, setText] = useState('')
  const [wahl, setWahl] = useState(0)
  const [meldung, setMeldung] = useState<Meldung | null>(null)
  const feld = useRef<HTMLTextAreaElement>(null)
  const ablage = useNotizAblage(meineWelt?.id ?? null)
  const markieren = useMarkieren()
  const status = useStatusSetzen()
  const mitschreiben = useMitschreiben()
  const qc = useQueryClient()
  const feldId = eingebettet ? `mitschrift-${laufend?.id ?? 'termin'}` : 'festhalten-feld'

  /** `auto` = was der Text sagt (☐ am Anfang); sonst hat der Schalter entschieden. */
  const [typ, setTyp] = useState<Typ>('auto')
  /** Die Fragen der Schnellen Aufgabe – `null` heißt „wie im Text". */
  const [wann, setWann] = useState<Zeitfenster | 'datum' | 'kein' | null>(null)
  const [datum, setDatum] = useState('')
  const [prio, setPrio] = useState<TaskPriority | ''>('')
  const [meinTag, setMeinTag] = useState<string | null>(null)
  const [welt, setWelt] = useState<string | null>(null)
  const darfMarkieren = useDarfMarkieren()

  const roh: Festgehalten = useMemo(() => festhaltenLesen(text, new Date()), [text])
  /** Als Aufgabe gewählt, ohne ☐ im Text: so lesen, als stünde es da (Frist, `heute`, `@`). */
  const gelesen: Festgehalten = useMemo(
    () =>
      typ === 'aufgabe' && roh.art !== 'aufgabe'
        ? festhaltenLesen(`☐ ${text.trim().replace(/^[?!]\s*/u, '')}`, new Date())
        : typ === 'notiz' && roh.art === 'aufgabe'
          ? { ...roh, art: 'notiz' as const, frist: null, fristText: null, heute: false }
          : roh,
    [roh, typ, text],
  )
  const ziele = useMemo(
    () =>
      eingebettet && laufend
        ? [{ art: 'termin' as const, termin: laufend, grund: 'läuft' }]
        : zieleBestimmen({ gelesen, laufend, puls, kommende }),
    [gelesen, laufend, puls, kommende, eingebettet],
  )
  const ziel: Ziel = ziele[wahl % ziele.length] ?? { art: 'zettel', grund: '' }
  const istAufgabe = gelesen.art === 'aufgabe' && !gelesen.fuer
  /** Wann, Prio und Mein Tag gelten nur für eigene Aufgaben – eine Unteraufgabe hat sie nicht. */
  const mitOptionen = istAufgabe && ziel.art !== 'aufgabe' && !eingebettet

  /** Wohin die Aufgabe kommt: gewählt › Vorschlag des Tages › zuletzt gewählt › persönliche Welt. */
  const eigeneWelten = welten.length ? welten : meineWelt ? [{ id: meineWelt.id, label: '' }] : []
  const zielWelt =
    eigeneWelten.find((w) => w.id === welt) ??
    eigeneWelten.find((w) => w.id === weltVorschlag) ??
    eigeneWelten.find((w) => w.id === weltGemerkt()) ??
    eigeneWelten.find((w) => w.id === meineWelt?.id) ??
    eigeneWelten[0] ??
    null

  const frist = wann === 'datum' ? datum || null : wann ? null : gelesen.frist
  const zeitfenster: Zeitfenster | null = wann && wann !== 'datum' && wann !== 'kein' ? wann : null
  const sonnenTag = meinTag !== null ? meinTag : gelesen.heute ? heuteSchluessel() : ''

  /** Meldungen verschwinden nach 5 s, solche mit „rückgängig" nach 10 s (im Browser getestet: 5 s waren knapp). */
  useEffect(() => {
    if (!meldung) return
    const t = window.setTimeout(() => setMeldung(null), meldung.rueckgaengig ? 10_000 : 5000)
    return () => window.clearTimeout(t)
  }, [meldung])

  /** **N** setzt den Cursor ins Feld, solange nichts anderes den Fokus hat. */
  useEffect(() => {
    if (blatt || eingebettet) return
    const taste = (e: KeyboardEvent) => {
      if (e.key !== 'n' || e.metaKey || e.ctrlKey || e.altKey) return
      const z = e.target as HTMLElement | null
      if (z?.closest('input, textarea, select, [contenteditable="true"], [role="dialog"]')) return
      e.preventDefault()
      feld.current?.focus()
    }
    window.addEventListener('keydown', taste)
    return () => window.removeEventListener('keydown', taste)
  }, [blatt, eingebettet])

  /** Eine Zeile ins Blatt des Termins – `false`, wenn das nicht geht (kein Netz, kein Notizbuch). */
  async function insBlatt(termin: StromTermin, zeile: string): Promise<boolean> {
    if (!ablage.freigegeben) return false
    if (typeof navigator !== 'undefined' && navigator.onLine === false) return false
    try {
      await mitschreiben({
        termin: {
          id: termin.id,
          titel: termin.betreff,
          beginn: termin.beginn,
          ende: termin.ende,
          ...(termin.teilnehmer?.length ? { teilnehmer: [...termin.teilnehmer] } : {}),
          serie: termin.serie ?? null,
        },
        blatt: blattZu?.(termin.id) ?? null,
        bevorzugt: ablage.ziel?.id ?? null,
        text: zeile,
      })
      return true
    } catch {
      return false
    }
  }

  const leeren = () => {
    setText('')
    setWahl(0)
    setTyp('auto')
    setWann(null)
    setDatum('')
    setPrio('')
    setMeinTag(null)
    setWelt(null)
  }

  async function aufgabeAnlegen(mitSonne: boolean) {
    const titel = gelesen.text
    /** Vor `leeren()` festhalten – danach sind die Knöpfe zurückgesetzt. */
    const wahlFrist = frist
    const wahlFenster = zeitfenster
    const wahlPrio = prio
    const wahlTag = mitSonne ? heuteSchluessel() : sonnenTag
    const wahlWelt = zielWelt
    if (titel.length < 3) {
      setMeldung({ text: 'Ein Titel mit drei Zeichen sagt mehr.' })
      return
    }
    leeren()
    if (ziel.art === 'aufgabe') {
      setMeldung({ text: `Unteraufgabe von „${ziel.titel}" …` })
      try {
        const { id } = await trpc.tasks.unteraufgabeAnlegen.mutate({
          taskId: ziel.id,
          title: titel,
        })
        setMeldung({
          text: `Unteraufgabe von „${ziel.titel}": ${titel}`,
          rueckgaengig: () => {
            void trpc.tasks.unteraufgabeLoeschen.mutate({ id })
            setMeldung({ text: 'Zurückgenommen.' })
          },
        })
      } catch (e) {
        setText(`☐ ${titel}`)
        setMeldung({ text: `Nicht angelegt: ${e instanceof Error ? e.message : 'Fehler'}` })
      }
      return
    }
    if (!wahlWelt || !ichId) {
      setText(`☐ ${titel}`)
      setMeldung({ text: 'Keine persönliche Arbeitswelt – Aufgaben gehen hier nicht.' })
      return
    }
    const herkunft =
      ziel.art === 'termin'
        ? `Festgehalten in „${ziel.termin.betreff}" am ${new Date().toLocaleDateString('de-DE')}.`
        : undefined
    setMeldung({ text: `Aufgabe „${titel}" …` })
    try {
      const { id } = await trpc.tasks.anlegen.mutate({
        title: titel,
        ...(herkunft ? { description: herkunft } : {}),
        ...(wahlFrist ? { dueAt: new Date(`${wahlFrist}T12:00:00`) } : {}),
        ...(wahlPrio ? { priority: wahlPrio } : {}),
        status: 'bereit',
        poolIds: [],
        zeitfenster: wahlFenster,
        groesse: null,
        mitOwnerPersonId: ichId,
        wunschPersonIds: [],
        vorhabenId: null,
        meilensteinId: null,
        assigneeId: ichId,
        workspaceId: wahlWelt.id,
      })
      if (wahlTag) markieren.mutate({ taskIds: [id], am: wahlTag })
      if (welt) weltMerken(welt)
      // Sofort im Vorlauf – wie nach ⚡ Schnelle Aufgabe.
      void nebenBestandVerwerfen(qc)
      if (ziel.art === 'termin') {
        amTermin?.(id, ziel.termin)
        void insBlatt(
          ziel.termin,
          `☐ ${titel}${wahlFrist ? ` · bis ${datumKurz(wahlFrist)}` : ''} · in Work`,
        )
      }
      setMeldung({
        text: `Aufgabe angelegt${eigeneWelten.length > 1 && wahlWelt.label ? ` in ${wahlWelt.label}` : ''}: ${titel}${wahlFrist ? ` · bis ${datumKurz(wahlFrist)}` : wahlFenster ? ` · ${ZEITFENSTER_KURZ[wahlFenster]}` : ''}${wahlPrio ? ` · Prio ${wahlPrio}` : ''}${wahlTag ? ` · ☀ ${wahlTag === heuteSchluessel() ? 'heute' : datumKurz(wahlTag)}` : ''}`,
        rueckgaengig: () => {
          status.mutate({ id, status: 'zurueckgestellt' })
          setMeldung({ text: 'Zurückgestellt.' })
        },
      })
    } catch (e) {
      setText(`☐ ${titel}`)
      setMeldung({ text: `Nicht angelegt: ${e instanceof Error ? e.message : 'Fehler'}` })
    }
  }

  async function notizAblegen() {
    if (!ablage.freigegeben || !ablage.ziel) {
      setMeldung({
        text: 'Notizen brauchen das Notizbuch. Mit ☐ am Anfang wird es eine Aufgabe.',
      })
      return
    }
    const eingabe = text.trim()
    const inhalt =
      gelesen.art === 'frage'
        ? `**Frage:** ${gelesen.text}`
        : gelesen.art === 'beschluss'
          ? `**Beschluss:** ${gelesen.text}`
          : gelesen.art === 'aufgabe'
            ? `- [ ] ${eingabe.replace(/^(?:☐|-\s*\[\s?\]|\[\s?\])\s*/u, '')}`
            : eingabe
    if (ziel.art === 'termin') {
      const zeile =
        gelesen.art === 'aufgabe'
          ? `[ ] ${eingabe.replace(/^(?:☐|-\s*\[\s?\]|\[\s?\])\s*/u, '')}`
          : inhalt.replace(/\s*\n\s*/g, ' ')
      leeren()
      if (await insBlatt(ziel.termin, zeile)) {
        if (!eingebettet) setMeldung({ text: `Mitgeschrieben bei ${ziel.termin.betreff}.` })
        return
      }
      // Ohne Netz oder bei einem Fehler: als eigene Notiz am Termin, wie bisher.
    }
    const ergebnis = ablage.ablegen({
      text: inhalt,
      termin: ziel.art === 'termin' ? terminKurz(ziel.termin) : null,
      aufgabeId: ziel.art === 'aufgabe' ? ziel.id : null,
      tags: gelesen.art === 'frage' ? [...gelesen.tags, 'frage'] : gelesen.tags,
    })
    if (ergebnis === 'nicht') {
      if (ziel.art === 'termin') setText(eingabe)
      setMeldung({ text: 'Nicht gespeichert – kein Netz und kein Gerätespeicher.' })
      return
    }
    leeren()
    if (ziel.art === 'aufgabe' && ergebnis === 'gespeichert') {
      // Sofort unter „Notizen zur Aufgabe" – der Server bestätigt hinterher.
      const schluessel = notizenZurAufgabeQuery(ziel.id).queryKey
      qc.setQueryData<NotizZurAufgabe[]>(schluessel, (alt) => [
        {
          notizbuchId: ablage.ziel?.id ?? '',
          notizId: `neu-${Date.now()}`,
          titel: inhalt.split('\n')[0]!.slice(0, 80),
          auszug: inhalt.slice(0, 160),
          datum: new Date().toISOString(),
          herkunft: false,
        },
        ...(alt ?? []),
      ])
      window.setTimeout(() => void qc.invalidateQueries({ queryKey: schluessel }), 2000)
    }
    setMeldung({
      text:
        ergebnis === 'wartet'
          ? 'Kein Netz – die Notiz wartet auf diesem Gerät.'
          : ziel.art === 'termin'
            ? `Notiert bei ${ziel.termin.betreff}.`
            : ziel.art === 'aufgabe'
              ? `Notiert zu „${ziel.titel}".`
              : 'Als Zettel abgelegt.',
    })
  }

  function ablegen(mitSonne: boolean) {
    if (!text.trim()) return
    if (istAufgabe) void aufgabeAnlegen(mitSonne)
    else void notizAblegen()
  }

  const zeigeChips = text.trim().length > 0

  /** Die Leiste in der Terminkarte: setzt das Zeichen an den Anfang (bzw. @ ans Ende). */
  const zeichen = (z: string) => {
    const t = text.replace(/^(?:☐|[!?])\s*/u, '')
    setText(z === '@' ? `${text}${text && !text.endsWith(' ') ? ' ' : ''}@` : `${z} ${t}`)
    feld.current?.focus()
  }

  if (eingebettet) {
    return (
      <div className="flex flex-col gap-1.5">
        <div className="flex items-start gap-2 rounded-[10px] border border-[#cfe6da] bg-surface px-2.5 focus-within:border-good">
          <label htmlFor={feldId} className="sr-only">
            Mitschreiben
          </label>
          <textarea
            id={feldId}
            ref={feld}
            rows={1}
            value={text}
            onChange={(e) => setText(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault()
                ablegen(e.metaKey || e.ctrlKey)
              } else if (e.key === 'Escape') {
                leeren()
              }
            }}
            placeholder="Mitschreiben … ↩"
            className="min-w-0 flex-1 resize-none border-0 bg-transparent py-2 text-[16px] leading-[1.4] text-ink outline-none [field-sizing:content] max-h-[120px]"
          />
        </div>
        <div className="flex flex-wrap items-center gap-1.5 text-[12px]">
          {(
            [
              ['☐', 'Aufgabe für dich'],
              ['!', 'Beschluss'],
              ['?', 'Frage für die nächste Runde'],
              ['@', 'Person'],
            ] as const
          ).map(([z, titel]) => (
            <button
              key={z}
              type="button"
              title={titel}
              aria-label={titel}
              onClick={() => zeichen(z)}
              className="flex h-8 min-w-9 cursor-pointer items-center justify-center rounded-lg border border-line bg-surface px-2 text-[14px] font-semibold text-ink2 hover:border-good"
            >
              {z}
            </button>
          ))}
          {zeigeChips && (
            <span
              className={cn(
                'rounded-full px-2 py-px font-semibold',
                istAufgabe ? 'bg-[#fdf3dc] text-[#8a6100]' : 'bg-[#f3f2ef] text-ink2',
              )}
            >
              {istAufgabe
                ? `☐ Aufgabe für dich${gelesen.fristText ? ` · ${gelesen.heute ? '☀ heute' : `bis ${datumKurz(gelesen.frist ?? '')}`}` : ''}`
                : gelesen.art === 'aufgabe'
                  ? `für ${gelesen.fuer} – nur notiert`
                  : gelesen.art === 'frage'
                    ? '? Frage'
                    : gelesen.art === 'beschluss'
                      ? '! Beschluss'
                      : 'Notiz'}
            </span>
          )}
          {meldung && (
            <span role="status" className="flex items-center gap-2 text-ink2">
              {meldung.text}
              {meldung.rueckgaengig && (
                <button
                  type="button"
                  onClick={meldung.rueckgaengig}
                  className="cursor-pointer font-semibold text-accent-deep"
                >
                  rückgängig
                </button>
              )}
            </span>
          )}
        </div>
      </div>
    )
  }

  return (
    <section
      aria-label="Festhalten"
      className={cn(
        'flex flex-col gap-2 rounded-card border bg-surface',
        blatt
          ? 'border-0 p-0'
          : 'border-[#d6e4f5] px-4 py-2.5 shadow-task focus-within:border-accent',
      )}
    >
      <div className="flex items-start gap-3">
        {!blatt && <Plus size={18} className="mt-[9px] shrink-0 text-accent" aria-hidden />}
        <label htmlFor={feldId} className="sr-only">
          Festhalten
        </label>
        <textarea
          id={feldId}
          ref={feld}
          rows={blatt ? 3 : 1}
          value={text}
          autoFocus={autoFocus}
          onChange={(e) => {
            setText(e.target.value)
            setWahl(0)
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault()
              ablegen(e.metaKey || e.ctrlKey)
            } else if (e.key === 'Tab' && !e.shiftKey && text.trim() && ziele.length > 1) {
              e.preventDefault()
              setWahl((w) => (w + 1) % ziele.length)
            } else if (e.key === 'Escape') {
              leeren()
            }
          }}
          placeholder={
            blatt
              ? 'Notiz, ☐ Aufgabe, ? Frage …'
              : 'Festhalten – Notiz, ☐ Aufgabe, ? Frage, ! Beschluss … (Taste N)'
          }
          className={cn(
            'min-w-0 flex-1 resize-none border-0 bg-transparent py-1.5 leading-[1.45] text-ink outline-none',
            blatt ? 'text-[17px]' : 'text-[16px] [field-sizing:content] max-h-[160px]',
          )}
        />
      </div>

      <div className={cn('flex flex-wrap items-center gap-1.5 text-[12px]', !blatt && 'pl-[30px]')}>
        {/* Notiz oder Aufgabe – der Schalter, ☐ am Anfang schaltet ihn auch. */}
        <div
          role="group"
          aria-label="Was es wird"
          className="flex overflow-hidden rounded-full border border-line2"
        >
          {(
            [
              ['notiz', 'Notiz'],
              ['aufgabe', '☐ Aufgabe'],
            ] as const
          ).map(([t, label]) => {
            const aktiv = (gelesen.art === 'aufgabe') === (t === 'aufgabe')
            return (
              <button
                key={t}
                type="button"
                aria-pressed={aktiv}
                onClick={() => {
                  setTyp(t)
                  feld.current?.focus()
                }}
                className={cn(
                  'cursor-pointer px-2.5 font-semibold',
                  blatt ? 'min-h-9 text-[14px]' : 'h-[24px]',
                  aktiv
                    ? t === 'aufgabe'
                      ? 'bg-[#fdf3dc] text-[#8a6100]'
                      : 'bg-ink text-white'
                    : 'bg-surface text-ink2 hover:bg-[#f3f2ef]',
                )}
              >
                {label}
              </button>
            )
          })}
        </div>
        {zeigeChips ? (
          <>
            {(gelesen.art !== 'notiz' || ziel.art === 'aufgabe') &&
              !(istAufgabe && ziel.art !== 'aufgabe') && (
                <span className="rounded-full bg-[#f3f2ef] px-2 py-px font-semibold text-ink2">
                  {istAufgabe
                    ? `☐ Unteraufgabe von „${ziel.art === 'aufgabe' ? ziel.titel : ''}"`
                    : gelesen.art === 'aufgabe'
                      ? `für ${gelesen.fuer} – nur notiert`
                      : gelesen.art === 'frage'
                        ? '? Frage'
                        : gelesen.art === 'beschluss'
                          ? '! Beschluss'
                          : 'Notiz'}
                </span>
              )}
            {gelesen.fristText && !mitOptionen && (
              <span className="rounded-full bg-[#f3f2ef] px-2 py-px text-ink2">
                {gelesen.heute ? '☀ heute' : `Frist ${datumKurz(gelesen.frist ?? '')}`}
              </span>
            )}
            {gelesen.personen.map((p) => (
              <span key={p} className="rounded-full bg-[#f3f2ef] px-2 py-px text-ink2">
                @{p}
              </span>
            ))}
            {!(mitOptionen && ziel.art === 'zettel') && (
              <button
                type="button"
                onClick={() => setWahl((w) => (w + 1) % ziele.length)}
                title={ziele.length > 1 ? '⇥ nächstes Ziel' : undefined}
                className={cn(
                  'flex cursor-pointer items-center gap-1 rounded-full px-2 py-px font-semibold',
                  ziel.art === 'zettel'
                    ? 'bg-[#f3f2ef] text-ink2'
                    : 'bg-accent-soft text-accent-deep',
                )}
              >
                {ziel.art === 'termin' && <span className="text-good">●</span>}
                gehört zu{' '}
                <b>
                  {ziel.art === 'termin'
                    ? ziel.termin.betreff
                    : ziel.art === 'aufgabe'
                      ? ziel.titel
                      : 'Zettel'}
                </b>
                <span className="font-normal">· {ziel.grund}</span>
                {ziele.length > 1 && <span aria-hidden="true"> ⇥</span>}
              </button>
            )}
          </>
        ) : (
          !blatt &&
          !meldung && (
            <span className="flex flex-wrap gap-x-3.5 pl-1.5 text-muted">
              <span>↩ ablegen</span>
              <span>⌘↩ + ☀ heute</span>
              <span>? Frage · ! Beschluss</span>
              <span>@ Person · # Tag</span>
              <span>⇥ anderes Ziel</span>
            </span>
          )
        )}
      </div>

      {mitOptionen && (
        <div
          className={cn(
            'grid gap-x-3 gap-y-2.5 rounded-xl bg-[#faf9f6] px-3 py-3',
            blatt ? 'grid-cols-1' : 'ml-[30px] grid-cols-[auto_minmax(0,1fr)] items-center',
          )}
        >
          {eigeneWelten.length > 1 && (
            <Zeile titel="Wo" blatt={blatt}>
              <Leiste titel="Wo">
                {eigeneWelten.map((w) => (
                  <Wahl
                    key={w.id}
                    blatt={blatt}
                    aktiv={zielWelt?.id === w.id}
                    onClick={() => setWelt(w.id)}
                  >
                    {w.icon ? `${w.icon} ` : ''}
                    {w.label}
                  </Wahl>
                ))}
              </Leiste>
            </Zeile>
          )}
          <Zeile titel="Wann" blatt={blatt}>
            <Leiste titel="Wann" umbrechen={blatt}>
              {ZEITFENSTER.map((z) => (
                <Wahl
                  key={z}
                  blatt={blatt}
                  aktiv={zeitfenster === z}
                  titel={ZEITFENSTER_LABELS[z]}
                  onClick={() => setWann(zeitfenster === z ? 'kein' : z)}
                  rot={z === 'jetzt'}
                >
                  {ZEITFENSTER_KURZ[z]}
                </Wahl>
              ))}
              <Wahl
                blatt={blatt}
                aktiv={!!frist || wann === 'datum'}
                titel="Ein bestimmtes Datum"
                onClick={() => {
                  if (wann === 'datum') setWann('kein')
                  else {
                    setDatum(frist ?? '')
                    setWann('datum')
                  }
                }}
              >
                {frist ? datumKurz(frist) : 'Datum'}
              </Wahl>
            </Leiste>
            {wann === 'datum' && (
              <input
                type="date"
                aria-label="Fällig am"
                value={datum}
                onChange={(e) => setDatum(e.target.value)}
                className={cn(
                  'mt-1.5 rounded-[9px] border border-line2 bg-surface px-2.5',
                  blatt ? 'h-11 w-full text-[16px]' : 'h-8 text-[13px]',
                )}
              />
            )}
          </Zeile>
          <Zeile titel="Prio" blatt={blatt}>
            <Leiste titel="Prio">
              {TASK_PRIORITIES.map((p) => (
                <Wahl
                  key={p}
                  blatt={blatt}
                  aktiv={prio === p}
                  onClick={() => setPrio(prio === p ? '' : p)}
                >
                  {p}
                </Wahl>
              ))}
            </Leiste>
          </Zeile>
          {darfMarkieren && (
            <Zeile titel="Mein Tag" blatt={blatt}>
              <Leiste titel="Mein Tag">
                {markierTage(heuteSchluessel()).map(({ stufe, tag }) => (
                  <Wahl
                    key={stufe}
                    blatt={blatt}
                    aktiv={sonnenTag === tag}
                    sonne
                    titel={tagKurz(tag)}
                    onClick={() => setMeinTag(sonnenTag === tag ? '' : tag)}
                  >
                    {stufe === 'heute' ? '☀ Heute' : MARKIER_STUFE_LABELS[stufe]}
                    {stufe !== 'heute' && tag !== naechsterKalendertag(stufe) && (
                      <span className="ml-1 font-normal opacity-70">{tagKurz(tag)}</span>
                    )}
                  </Wahl>
                ))}
              </Leiste>
            </Zeile>
          )}
        </div>
      )}

      {blatt && (
        <button
          type="button"
          onClick={() => ablegen(false)}
          disabled={!text.trim()}
          className="min-h-[52px] cursor-pointer rounded-[14px] bg-accent text-[16px] font-bold text-white disabled:opacity-40"
        >
          {istAufgabe ? 'Aufgabe anlegen' : 'Ablegen'}
        </button>
      )}

      {meldung && (
        <div
          role="status"
          className={cn(
            'flex flex-wrap items-center gap-2 text-[12.5px] text-ink2',
            !blatt && 'pl-[30px]',
          )}
        >
          <span>{meldung.text}</span>
          {meldung.rueckgaengig && (
            <button
              type="button"
              onClick={meldung.rueckgaengig}
              className="cursor-pointer font-semibold text-accent-deep"
            >
              rückgängig
            </button>
          )}
        </div>
      )}
    </section>
  )
}

const datumKurz = (tag: string) =>
  tag
    ? new Date(`${tag}T12:00:00`).toLocaleDateString('de-DE', {
        weekday: 'short',
        day: 'numeric',
        month: 'numeric',
      })
    : ''

/** Eine Zeile des Formulars: am Rechner Beschriftung links, am Telefon darüber. */
function Zeile({ titel, blatt, children }: { titel: string; blatt: boolean; children: ReactNode }) {
  return (
    <>
      <span
        className={cn('font-semibold text-ink2', blatt ? '-mb-1.5 text-[13px]' : 'text-[12px]')}
      >
        {titel}
      </span>
      <div className="min-w-0">{children}</div>
    </>
  )
}

/**
 * **Eine Leiste wie bei ⚡ Schnelle Aufgabe** – die Knöpfe hängen aneinander, die Trennlinien
 * sind der Hintergrund (`gap-px` auf `bg-line2`), so bricht sie am Telefon sauber um.
 */
function Leiste({
  titel,
  umbrechen = false,
  children,
}: {
  titel: string
  umbrechen?: boolean
  children: ReactNode
}) {
  return (
    <div
      role="group"
      aria-label={titel}
      className={cn(
        'flex gap-px overflow-hidden rounded-[9px] border border-line2 bg-line2',
        umbrechen ? 'flex-wrap' : 'w-fit max-w-full flex-wrap',
      )}
    >
      {children}
    </div>
  )
}

function Wahl({
  aktiv,
  blatt,
  titel,
  rot = false,
  sonne = false,
  onClick,
  children,
}: {
  aktiv: boolean
  blatt: boolean
  titel?: string
  rot?: boolean
  sonne?: boolean
  onClick: () => void
  children: ReactNode
}) {
  return (
    <button
      type="button"
      aria-pressed={aktiv}
      title={titel}
      onClick={onClick}
      className={cn(
        'cursor-pointer px-2.5 font-semibold whitespace-nowrap',
        blatt ? 'min-h-11 grow basis-[30%] text-[14.5px]' : 'h-8 text-[12.5px]',
        aktiv
          ? sonne
            ? 'bg-sonne-bg text-sonne'
            : rot
              ? 'bg-pool-sofort text-white'
              : 'bg-ink text-white'
          : 'bg-surface text-ink2 hover:bg-[#f3f2ef]',
      )}
    >
      {children}
    </button>
  )
}

/** Heißt „Morgen" wirklich morgen? Am Freitag/Samstag ist es der Montag – dann steht der Tag dabei. */
function naechsterKalendertag(stufe: string): string {
  const d = new Date()
  d.setDate(d.getDate() + (stufe === 'morgen' ? 1 : 2))
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
}

const WELT_GEMERKT = 'netcon-work:festhalten-welt'

function weltGemerkt(): string | null {
  try {
    return window.localStorage.getItem(WELT_GEMERKT)
  } catch {
    return null
  }
}

function weltMerken(id: string): void {
  try {
    window.localStorage.setItem(WELT_GEMERKT, id)
  } catch {
    // Ohne Gerätespeicher gilt beim nächsten Mal wieder die persönliche Welt.
  }
}
